import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { normalizeSalaryConfigData } from '../../lib/api/salaryConfig'
import { calculateDayPayroll } from '../../lib/api/salaryCalc'
import './DailyReportPage.css'

const formatMoney = (val) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(val || 0)

const formatHours = (h) => {
  const rounded = Math.round((Number(h) || 0) * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
}

export function formatTimeInTimeZone(ts, timeZone = 'Europe/Moscow') {
  if (!ts) return ''
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: timeZone || 'Europe/Moscow',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(ts))
  } catch (e) {
    const d = new Date(ts)
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  }
}

export function isQualifyingShift(s) {
  if (!s) return false
  if (s.started_at !== null) return true
  if (s.is_planned === true) return false
  const h = s.hours != null ? parseFloat(s.hours) : 0
  if (h > 0 && s.approval_status !== 'pending' && s.approval_status !== 'rejected') {
    return true
  }
  return false
}

export function deriveActualShiftSummary(schedules = [], incomingShiftSummary = null) {
  const qualifying = (schedules || []).filter(isQualifyingShift)
  const openCount = qualifying.filter((s) => s.started_at !== null && s.ended_at === null).length
  const closedCount = qualifying.length - openCount

  // Если incomingShiftSummary уже предоставлен и согласован с числом квалифицированных смен
  if (incomingShiftSummary && incomingShiftSummary.totalShifts === qualifying.length) {
    return {
      ...incomingShiftSummary,
      workedShifts: incomingShiftSummary.workedShifts ?? incomingShiftSummary.totalShifts,
      completedShifts: incomingShiftSummary.completedShifts ?? incomingShiftSummary.closedShifts
    }
  }

  const uniqueEmps = new Set(qualifying.map((s) => s.employee_id).filter(Boolean))
  const totalHours = qualifying.reduce((acc, s) => {
    const h = parseFloat(s.hours)
    return acc + (!isNaN(h) && h > 0 ? h : 0)
  }, 0)

  return {
    totalShifts: qualifying.length,
    workedShifts: qualifying.length,
    closedShifts: closedCount,
    completedShifts: closedCount,
    openShifts: openCount,
    uniqueEmployees: uniqueEmps.size,
    totalHours: Math.round(totalHours * 10) / 10,
    plannedShifts: (schedules || []).filter((s) => s.is_planned === true).length,
    noShowPlannedShifts: (schedules || []).filter((s) => s.is_planned === true && !s.started_at).length
  }
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  return dt.toLocaleDateString('ru-RU', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long'
  })
}

export default function DailyReportPage({ pageParams, onBack }) {
  const { activePvz, isOwner, isAdmin } = useAuth()

  // 1. Извлекаем контекст PVZ и даты (приоритет: pageParams -> URLSearchParams -> активный ПВЗ)
  const [reportContext, setReportContext] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search)
    let stored = null
    try {
      const raw = localStorage.getItem('wb_daily_report_navigate')
      if (raw) {
        stored = JSON.parse(raw)
        localStorage.removeItem('wb_daily_report_navigate')
      }
    } catch (e) {}

    const pvzId = pageParams?.pvzId || searchParams.get('pvzId') || stored?.pvzId || activePvz?.id || ''
    const todayStr = new Date().toISOString().split('T')[0]
    const date = pageParams?.date || searchParams.get('date') || stored?.date || todayStr

    return { pvzId, date }
  })

  // Слушатель системных событий навигации от SW
  useEffect(() => {
    const handleSwNav = (e) => {
      const detail = e?.detail
      if (detail && detail.date) {
        setReportContext({
          pvzId: detail.pvzId || activePvz?.id || '',
          date: detail.date
        })
      }
    }
    window.addEventListener('daily-report-navigate', handleSwNav)
    return () => window.removeEventListener('daily-report-navigate', handleSwNav)
  }, [activePvz?.id])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reportData, setReportData] = useState(null)

  // 2. Загрузка данных отчета с бэкенда
  const fetchReport = useCallback(async () => {
    if (!reportContext.pvzId || !reportContext.date) {
      setLoading(false)
      setError('Не указан пункт выдачи или дата для формирования отчета')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await api.getDailyReport(reportContext.pvzId, reportContext.date)
      setReportData(data)
    } catch (err) {
      console.error('[DailyReport] Fetch error:', err)
      setError(err.message || 'Ошибка загрузки отчета за день')
    } finally {
      setLoading(false)
    }
  }, [reportContext.pvzId, reportContext.date])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  // 3. Канонический расчет зарплат сотрудников (только если canSeePayroll === true)
  // ИСПОЛЬЗУЕТСЯ СТРОГО ЕДИНЫЙ ДВИЖОК calculateSalaryFromData (через чистый хелпер calculateDayPayroll)
  // С РЕЖИМОМ readOnly: true И strictHistoricalCoverage: true
  const salaryResults = useMemo(() => {
    if (!reportData || !reportData.canSeePayroll || !reportData.salaryConfig) {
      return {
        employeeEarnings: {},
        totalDailyPayroll: null,
        payrollHistoryReliable: true,
        payrollUnavailableReason: null,
        earliestReliableDate: null
      }
    }

    const {
      employees = [],
      workLogs = [],
      schedules = [],
      payments = [],
      deductions = [],
      salaryConfig,
      salaryConfigHistory = [],
      actualWorkedEmployeeIds,
      businessDate,
      pvz
    } = reportData

    return calculateDayPayroll({
      dayDate: businessDate,
      employees,
      schedules,
      workLogs,
      payments,
      deductions,
      salaryConfig,
      salaryConfigHistory,
      pvzTimezone: pvz?.timezone || 'Europe/Moscow',
      actualWorkedEmployeeIds
    })
  }, [reportData])


  // 4. Единый канонический расчет оперативных показателей смен по фактически отработанным сменам
  const shiftSummary = useMemo(() => {
    if (!reportData) return null
    return deriveActualShiftSummary(reportData.schedules, reportData.shiftSummary)
  }, [reportData?.schedules, reportData?.shiftSummary])

  // Обработка кнопки «Назад»
  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      window.history.back()
    }
  }

  // Состояние загрузки
  if (loading) {
    return (
      <div className="daily-report-page">
        <div className="daily-report-loading">
          <div className="app-loading-spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Формирование отчета за рабочий день...</p>
        </div>
      </div>
    )
  }

  // Состояние ошибки
  if (error || !reportData) {
    return (
      <div className="daily-report-page">
        <div className="daily-report-error">
          <div className="daily-report-error-card">
            <h3 style={{ color: 'var(--danger)', marginTop: 0 }}>Не удалось загрузить отчет</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.5 }}>
              {error || 'Данные за указанную дату не найдены'}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
              <button className="btn btn-secondary" onClick={handleBack}>
                Вернуться
              </button>
              <button className="btn btn-primary" onClick={fetchReport}>
                Повторить попытку
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const {
    pvz,
    businessDate,
    operationSummary,
    expensesTotal,
    schedules = [],
    workLogs = [],
    expenses = [],
    canSeePayroll
  } = reportData

  // Определение отработавших сотрудников с их сменами и операциями
  const workingEmployeesMap = new Map()

  // Учитываем только квалифицированные фактически отработанные смены
  const qualifyingSchedules = (schedules || []).filter(isQualifyingShift)

  qualifyingSchedules.forEach((s) => {
    if (s.employee_id) {
      if (!workingEmployeesMap.has(s.employee_id)) {
        workingEmployeesMap.set(s.employee_id, {
          id: s.employee_id,
          name: s.employee_name || 'Неизвестный сотрудник',
          role: s.shift_role || s.employee_profile_role || 'employee',
          shifts: [],
          totalHours: 0,
          accepted: 0,
          issued: 0,
          returns: 0
        })
      }
      const emp = workingEmployeesMap.get(s.employee_id)
      emp.shifts.push(s)
      const h = parseFloat(s.hours) || 0
      emp.totalHours += h
    }
  })

  // Агрегируем операции только для сотрудников с квалифицированной сменой
  // (work_logs не должны создавать новых сотрудников без квалифицированной смены)
  workLogs.forEach((wl) => {
    if (wl.employee_id && workingEmployeesMap.has(wl.employee_id)) {
      const emp = workingEmployeesMap.get(wl.employee_id)
      const count = Number(wl.count) || 0
      if (wl.type === 'acceptance') emp.accepted += count
      else if (wl.type === 'issued') emp.issued += count
      else if (wl.type === 'returns') emp.returns += count
    }
  })

  const workingEmployees = Array.from(workingEmployeesMap.values())
  const hasActivity = shiftSummary.totalShifts > 0 || operationSummary.total > 0 || expenses.length > 0

  // Расчет общих прямых затрат дня
  const totalDirectCosts =
    canSeePayroll && salaryResults.payrollHistoryReliable !== false && salaryResults.totalDailyPayroll !== null
      ? Math.round((salaryResults.totalDailyPayroll + expensesTotal) * 100) / 100
      : null

  return (
    <div className="daily-report-page animate-fade-in">
      {/* Шапка отчета */}
      <div className="daily-report-header">
        <button className="daily-report-back-btn" onClick={handleBack} type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Назад
        </button>

        <div className="daily-report-title-row">
          <div>
            <h1 className="daily-report-title">Сводка дня: {pvz?.name || 'ПВЗ'}</h1>
            <p className="daily-report-subtitle">{formatDisplayDate(businessDate)}</p>
          </div>

          <div>
            {shiftSummary.totalShifts > 0 && shiftSummary.openShifts === 0 ? (
              <span className="daily-report-badge badge-success">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Все смены закрыты ({shiftSummary.closedShifts}/{shiftSummary.totalShifts})
              </span>
            ) : shiftSummary.openShifts > 0 ? (
              <span className="daily-report-badge badge-warning">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Открыто смен: {shiftSummary.openShifts}
              </span>
            ) : (
              <span className="daily-report-badge badge-neutral">Нет зарегистрированных смен</span>
            )}
          </div>
        </div>
      </div>

      {/* Ключевые показатели дня (KPI Grid) */}
      <div className="daily-report-kpi-grid">
        <div className="daily-report-kpi-card">
          <span className="kpi-label">Смены</span>
          <span className="kpi-value">
            {shiftSummary.closedShifts} / {shiftSummary.totalShifts}
          </span>
          <span className="kpi-hint">
            {shiftSummary.uniqueEmployees > 0
              ? `${shiftSummary.uniqueEmployees} сотр. на смене`
              : 'Сотрудники не выходили'}
          </span>
        </div>

        <div className="daily-report-kpi-card">
          <span className="kpi-label">Отработано часов</span>
          <span className="kpi-value text-primary">{formatHours(shiftSummary.totalHours)} ч</span>
          <span className="kpi-hint">Суммарное время смен</span>
        </div>

        <div className="daily-report-kpi-card">
          <span className="kpi-label">Физические операции</span>
          <span className="kpi-value">
            {operationSummary.physicalCoreTotal ?? (operationSummary.accepted + operationSummary.issued + operationSummary.returns)} шт
          </span>
          <span className="kpi-hint">
            Выдача + Приёмка + Возвраты{operationSummary.custom > 0 ? ` (+${operationSummary.custom} прочих)` : ''}
          </span>
        </div>

        {canSeePayroll && (
          <div className="daily-report-kpi-card">
            <span className="kpi-label">ФОТ за день</span>
            {salaryResults.payrollHistoryReliable === false ? (
              <>
                <span className="kpi-value text-muted" style={{ fontSize: '15px' }}>Недоступен</span>
                <span className="kpi-hint">
                  {salaryResults.earliestReliableDate
                    ? `Тарифы действуют с ${salaryResults.earliestReliableDate}`
                    : 'Нет истории тарифов'}
                </span>
              </>
            ) : salaryResults.totalDailyPayroll !== null ? (
              <>
                <span className="kpi-value text-success">{formatMoney(salaryResults.totalDailyPayroll)}</span>
                <span className="kpi-hint">Начислено сотрудникам</span>
              </>
            ) : (
              <>
                <span className="kpi-value text-muted">—</span>
                <span className="kpi-hint">Нет начислений</span>
              </>
            )}
          </div>
        )}
      </div>

      {!hasActivity ? (
        <div className="daily-report-section">
          <div className="daily-report-empty">
            <div className="daily-report-empty-icon">📅</div>
            <h3>Нет данных за этот день</h3>
            <p>В выбранную дату на пункте выдачи не было зафиксировано смен, операций или расходов.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Секция: Отработавшие сотрудники */}
          <div className="daily-report-section">
            <h2 className="daily-report-section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Сотрудники на смене ({workingEmployees.length})
            </h2>

            {workingEmployees.length === 0 ? (
              <div className="text-secondary" style={{ fontSize: '14px' }}>
                В этот день не было зафиксировано выходов сотрудников.
              </div>
            ) : (
              <div className="daily-report-emp-list">
                {workingEmployees.map((emp) => {
                  const earnings = salaryResults.employeeEarnings[emp.id]
                  const pvzTz = pvz?.timezone || 'Europe/Moscow'
                  const shiftTimes = emp.shifts
                    .map((s) => {
                      if (s.started_at && s.ended_at) {
                        return `${formatTimeInTimeZone(s.started_at, pvzTz)} – ${formatTimeInTimeZone(s.ended_at, pvzTz)}`
                      } else if (s.started_at && !s.ended_at) {
                        return `с ${formatTimeInTimeZone(s.started_at, pvzTz)} (на смене)`
                      }
                      return null
                    })
                    .filter(Boolean)
                    .join(', ')

                  return (
                    <div key={emp.id} className="daily-report-emp-card">
                      <div className="emp-main-info">
                        <span className="emp-name">{emp.name}</span>
                        <span className="emp-role-badge">
                          {emp.role === 'helper'
                            ? 'Помощник'
                            : emp.role === 'admin' || emp.role === 'admin_employee'
                            ? 'Администратор'
                            : 'Оператор'}
                        </span>
                      </div>

                      <div className="emp-shift-details">
                        {shiftTimes && <div>Время: <strong>{shiftTimes}</strong></div>}
                        <div>
                          Отработано: <strong>{formatHours(emp.totalHours)} ч</strong>
                        </div>
                      </div>

                      <div className="emp-ops-summary">
                        <span className="emp-ops-item" title="Выдано заказов">
                          Выдано: <strong>{emp.issued}</strong>
                        </span>
                        <span>·</span>
                        <span className="emp-ops-item" title="Принято отправлений">
                          Принято: <strong>{emp.accepted}</strong>
                        </span>
                        <span>·</span>
                        <span className="emp-ops-item">
                          Возвраты: <strong>{emp.returns}</strong>
                        </span>
                      </div>

                      {canSeePayroll && earnings && (
                        <div className="emp-salary-block">
                          {earnings.payrollReliable !== false ? (
                            <>
                              <span className={`emp-salary-amount ${earnings.total < 0 ? 'is-negative' : ''}`}>
                                {formatMoney(earnings.total)}
                              </span>
                              <span className="emp-salary-breakdown">
                                Оклад {formatMoney(earnings.basePay)}
                                {(earnings.acceptBonus > 0 || earnings.issuedBonus > 0 || earnings.returnBonus > 0) &&
                                  ` + Бонус за операции ${formatMoney(earnings.acceptBonus + earnings.issuedBonus + earnings.returnBonus)}`}
                                {earnings.customWorkBonus > 0 &&
                                  ` + Доплаты ${formatMoney(earnings.customWorkBonus)}`}
                                {earnings.totalBonuses > 0 &&
                                  ` + Премии ${formatMoney(earnings.totalBonuses)}`}
                                {earnings.helperDeduction > 0 &&
                                  ` − Помощник ${formatMoney(earnings.helperDeduction)}`}
                                {earnings.totalFines > 0 &&
                                  ` − Штрафы ${formatMoney(earnings.totalFines)}`}
                                {earnings.totalWbDeductions > 0 &&
                                  ` − Удержания ${formatMoney(earnings.totalWbDeductions)}`}
                              </span>
                              {earnings.deductionDetails?.length > 0 && (
                                <div className="emp-salary-subtext">
                                  {earnings.deductionDetails.map((d, i) => (
                                    <span key={d.id || i} className="emp-salary-note-item">
                                      Удержание ({d.type || 'прочее'}): {d.comment || 'без описания'} ({formatMoney(d.amount)})
                                    </span>
                                  ))}
                                </div>
                              )}
                              {earnings.fineDetails?.length > 0 && (
                                <div className="emp-salary-subtext">
                                  {earnings.fineDetails.map((f, i) => (
                                    <span key={f.id || i} className="emp-salary-note-item">
                                      Штраф: {f.comment || 'без описания'} ({formatMoney(f.amount)})
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="emp-salary-amount text-muted" style={{ fontSize: '13px', fontWeight: 500 }}>
                              ФОТ недоступен — нет истории тарифов
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Секция: Физические операции */}
          <div className="daily-report-section">
            <h2 className="daily-report-section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
              </svg>
              Операционная активность ПВЗ
            </h2>

            <div className="daily-report-ops-grid">
              <div className="ops-card">
                <span className="ops-card-label">Выдано заказов</span>
                <span className="ops-card-value">{operationSummary.issued} шт</span>
              </div>

              <div className="ops-card">
                <span className="ops-card-label">Принято отправлений</span>
                <span className="ops-card-value">
                  {operationSummary.accepted} шт
                  {operationSummary.acceptedLate > 0 && (
                    <span style={{ fontSize: '11px', color: 'var(--danger)', marginLeft: '4px' }}>
                      (просрочено: {operationSummary.acceptedLate})
                    </span>
                  )}
                </span>
              </div>

              <div className="ops-card">
                <span className="ops-card-label">Оформлено возвратов</span>
                <span className="ops-card-value">{operationSummary.returns} шт</span>
              </div>

              {operationSummary.custom > 0 && (
                <div className="ops-card">
                  <span className="ops-card-label">Прочие операции</span>
                  <span className="ops-card-value">{operationSummary.custom} шт</span>
                </div>
              )}

              <div className="ops-card" style={{ background: 'rgba(203, 17, 171, 0.05)', borderColor: 'rgba(203, 17, 171, 0.2)' }}>
                <span className="ops-card-label" style={{ fontWeight: 600 }}>Всего операций</span>
                <span className="ops-card-value" style={{ color: 'var(--wb-primary)' }}>{operationSummary.total} шт</span>
              </div>
            </div>
          </div>

          {/* Секция: Прямые затраты дня */}
          <div className="daily-report-section">
            <h2 className="daily-report-section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              Затраты за рабочий день
            </h2>

            <div className="expenses-list">
              {canSeePayroll && salaryResults.payrollHistoryReliable === false && (
                <div className="expense-row">
                  <span className="expense-name">Фонд оплаты труда (сдельные начисления смен)</span>
                  <span className="expense-val text-muted" style={{ fontStyle: 'italic', fontSize: '13px' }}>
                    Недоступен (нет истории тарифов)
                  </span>
                </div>
              )}
              {canSeePayroll && salaryResults.payrollHistoryReliable !== false && salaryResults.totalDailyPayroll !== null && (
                <div className="expense-row">
                  <span className="expense-name">Фонд оплаты труда (сдельные начисления смен)</span>
                  <span className="expense-val">-{formatMoney(salaryResults.totalDailyPayroll)}</span>
                </div>
              )}

              {expenses.length === 0 ? (
                <div className="text-secondary" style={{ fontSize: '13px', padding: '4px 0' }}>
                  В этот день не было зарегистрировано хозяйственных или переменных расходов.
                </div>
              ) : (
                expenses.map((exp) => (
                  <div key={exp.id} className="expense-row">
                    <span className="expense-name">
                      {exp.category ? `[${exp.category}] ` : ''}
                      {exp.comment || 'Переменный расход'}
                    </span>
                    <span className="expense-val">-{formatMoney(exp.amount)}</span>
                  </div>
                ))
              )}
            </div>

            {totalDirectCosts !== null && (
              <div className="costs-summary-box">
                <span>Итого прямых затрат за день:</span>
                <span className="costs-total-val">-{formatMoney(totalDirectCosts)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
